import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, GoogleLogo } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Brand from "@/components/Brand";

const BG = "https://images.unsplash.com/photo-1765164478579-2c041dad2c7d?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85";

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", organization: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Clariva");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const googleSignIn = () => {
    const redirect = `${window.location.origin}/auth/google/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 -z-10">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg)]/40 to-[var(--bg)]" />
      </div>
      <div className="min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ y: 40, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md glass-strong p-8"
        >
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Brand size={32} />
            <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Clariva</div>
          </Link>

          <div className="overline mb-2">Create account</div>
          <h1 className="text-3xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Get started in 30 seconds</h1>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label className="text-xs text-[var(--text-2)]">Full name</Label>
              <Input value={form.full_name} onChange={set("full_name")} required data-testid="register-fullname-input" className="mt-1.5 glass-subtle border-[var(--border)]" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">Work email</Label>
              <Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email-input" className="mt-1.5 glass-subtle border-[var(--border)]" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">Organization</Label>
              <Input value={form.organization} onChange={set("organization")} placeholder="Optional" data-testid="register-org-input" className="mt-1.5 glass-subtle border-[var(--border)]" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">Password</Label>
              <Input type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password-input" className="mt-1.5 glass-subtle border-[var(--border)]" />
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full h-11 gap-2">
              {loading ? "Creating…" : <>Create account <ArrowRight size={14} /></>}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="text-xs text-[var(--text-3)]">OR</span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          <Button onClick={googleSignIn} variant="outline" data-testid="register-google-btn" className="w-full h-11 gap-2 glass-subtle border-[var(--border-strong)]">
            <GoogleLogo size={16} weight="bold" /> Continue with Google
          </Button>

          <div className="mt-6 text-sm text-[var(--text-2)] text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-[var(--accent)] hover:underline" data-testid="login-link">Sign in</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
